import CloseIcon from '@mui/icons-material/Close';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import type { CurveId } from '../config/plotTypes';
import {
  areExportTimeRangesEqual,
  formatExportTimeRange,
} from './formatExportTimeRange';
import type {
  ExportCsvContextProps,
  ExportCsvDecimationSettings,
  ExportCsvFormValues,
  ExportCsvTimeRangeChoice,
} from './types';

// -- type definitions --

export interface ExportCsvDialogProps extends ExportCsvContextProps {
  open: boolean;
  onClose: () => void;
}

// -- constants --

const DEFAULT_DECIMATION_POINTS = 2000;

// -- helper functions --

function buildInitialFormValues(curveIds: CurveId[]): ExportCsvFormValues {
  return {
    selectedCurveIds: curveIds,
    timeRangeChoice: 'loaded',
    decimation: { enabled: true, points: DEFAULT_DECIMATION_POINTS },
  };
}

function getCurveLabel(
  curveId: CurveId,
  curvePrefs: ExportCsvContextProps['curvePrefs']
): string {
  return curvePrefs[curveId]?.displayName || curveId;
}

// -- main component --

/**
 * Modal form for choosing CSV export options.
 */
export const ExportCsvDialog: React.FC<ExportCsvDialogProps> = ({
  open,
  onClose,
  curvePrefs,
  timeRanges,
  onExportCsv,
}) => {
  const curveIds = useMemo(() => Object.keys(curvePrefs), [curvePrefs]);
  const visibleSameAsLoaded = areExportTimeRangesEqual(
    timeRanges.loaded,
    timeRanges.visible
  );

  const [selectedCurveIds, setSelectedCurveIds] = useState<CurveId[]>(curveIds);
  const [timeRangeChoice, setTimeRangeChoice] =
    useState<ExportCsvTimeRangeChoice>('loaded');
  const [decimation, setDecimation] = useState<ExportCsvDecimationSettings>({
    enabled: true,
    points: DEFAULT_DECIMATION_POINTS,
  });
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    const initialValues = buildInitialFormValues(curveIds);
    setSelectedCurveIds(initialValues.selectedCurveIds);
    setTimeRangeChoice(initialValues.timeRangeChoice);
    setDecimation(initialValues.decimation);
    setExportError(null);
  }, [curveIds, open]);

  const handleSelectAllCurves = () => {
    setSelectedCurveIds(curveIds);
  };

  const handleSelectedCurvesChange = (
    _event: React.SyntheticEvent,
    nextValue: CurveId[]
  ) => {
    setSelectedCurveIds(nextValue);
  };

  const handleTimeRangeChoiceChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nextChoice = event.target.value as ExportCsvTimeRangeChoice;
    setTimeRangeChoice(nextChoice);
  };

  const handleDecimationToggle = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const enabled = event.target.checked;
    const nextDecimation: ExportCsvDecimationSettings = enabled
      ? { enabled: true, points: DEFAULT_DECIMATION_POINTS }
      : { enabled: false };

    setDecimation(nextDecimation);
  };

  const handleDecimationPointsChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const parsedPoints = Number.parseInt(event.target.value, 10);
    const points =
      Number.isFinite(parsedPoints) && parsedPoints > 0
        ? parsedPoints
        : DEFAULT_DECIMATION_POINTS;

    setDecimation({ enabled: true, points });
  };

  const handleExportClick = async () => {
    if (!onExportCsv || selectedCurveIds.length === 0) {
      return;
    }

    setIsExporting(true);
    setExportError(null);

    try {
      await onExportCsv({
        selectedCurveIds,
        timeRangeChoice,
        decimation,
      });
      onClose();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "L'export CSV a échoué";
      setExportError(message);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pr: 6 }}>
        Exporter les données en CSV
        <IconButton
          aria-label="Fermer"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={3}>
          <Box>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
            >
              <Typography variant="subtitle2">Sources de données</Typography>
              <Button size="small" onClick={handleSelectAllCurves}>
                Tout sélectionner
              </Button>
            </Box>
            <Autocomplete
              multiple
              options={curveIds}
              getOptionLabel={(option) => getCurveLabel(option, curvePrefs)}
              value={selectedCurveIds}
              onChange={handleSelectedCurvesChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Courbes à exporter"
                  size="small"
                  placeholder="Sélectionner des courbes"
                />
              )}
            />
          </Box>

          <FormControl>
            <FormLabel>Période à exporter</FormLabel>
            <RadioGroup
              value={timeRangeChoice}
              onChange={handleTimeRangeChoiceChange}
            >
              <FormControlLabel
                value="loaded"
                control={<Radio size="small" />}
                label={`Plage chargée (${formatExportTimeRange(
                  timeRanges.loaded
                )})`}
              />
              <FormControlLabel
                value="visible"
                control={<Radio size="small" />}
                disabled={visibleSameAsLoaded || !timeRanges.visible}
                label={`Plage visible (${formatExportTimeRange(
                  timeRanges.visible
                )})`}
              />
            </RadioGroup>
          </FormControl>

          <Box display="flex" flexDirection="column" gap={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={decimation.enabled}
                  onChange={handleDecimationToggle}
                  size="small"
                />
              }
              label="Nombre maximum de point (utilise des moyennes de points)"
            />
            <TextField
              label="Nombre de points"
              type="number"
              size="small"
              value={decimation.enabled ? decimation.points : ''}
              disabled={!decimation.enabled}
              onChange={handleDecimationPointsChange}
              slotProps={{ htmlInput: { min: 1 } }}
              sx={{ maxWidth: 220 }}
            />
          </Box>
          {exportError && (
            <Typography color="error" variant="body2">
              {exportError}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isExporting}>
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={handleExportClick}
          disabled={
            isExporting || !onExportCsv || selectedCurveIds.length === 0
          }
        >
          {isExporting ? 'Export en cours…' : 'Exporter'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
